import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import api from '../api';

const BLUE = '#2B7FFF';
const GOLD = '#C9A84C';
const OFF_WHITE = '#A0AABB';
const BORDER = 'rgba(255,255,255,0.07)';
const CARD = '#0F1929';
const SURFACE = '#0B1120';

// Profession configs
const PROFESSIONS = {
  barbers: {
    label: 'Barbers',
    headline: 'Built for Barbers.',
    sub: 'Stop losing your income to apps that take a cut. Own your booking, your clients, and your business.',
    pain: 'Barbershop apps charge commissions, own your client list, and show your chair next to competitors.',
    features: [
      'Online booking — clients book you directly, 24/7',
      'Automated SMS reminders — no more no-shows',
      'AI re-engagement — brings lapsed clients back',
      'Client history — every cut, every note, every visit',
      'Analytics — see your busiest days and top services',
      '$0 commission — your money stays yours',
    ],
    cta: 'Claim Your Barber Spot',
    ogImage: '/og-barbers.png',
    ogTitle: 'Built for Barbers — Mane',
    ogDesc: 'Stop losing money to booking apps. Online booking, reminders, and AI tools for barbers. $29/mo. $0 commission.',
    accent: BLUE,
  },
  'nail-techs': {
    label: 'Nail Techs',
    headline: 'Built for Nail Techs.',
    sub: 'Your chair. Your clients. Your income. Run everything from one place — no commissions, no middleman.',
    pain: 'Nail booking platforms charge fees and own your client relationships. You do the work, they take the cut.',
    features: [
      'Online booking — clients book your services directly',
      'Automated reminders — reduce no-shows automatically',
      'AI follow-ups — win back clients who haven\'t returned',
      'Client history — track every service and preference',
      'Custom booking page — your name, your brand',
      '$0 commission — you keep every dollar',
    ],
    cta: 'Claim Your Nail Tech Spot',
    ogImage: '/og-nail-techs.png',
    ogTitle: 'Built for Nail Techs — Mane',
    ogDesc: 'Your chair. Your clients. Your income. Booking, reminders, and client management for nail techs. $29/mo. $0 commission.',
    accent: '#C084FC',
  },
  stylists: {
    label: 'Hair Stylists',
    headline: 'Built for Hair Stylists.',
    sub: 'Run your suite like a real business. Booking, deposits, client history — all in one place, all yours.',
    pain: 'Suite owners juggle 3 apps to manage their business. Client data is scattered and no one follows up automatically.',
    features: [
      'Online booking — link directly from Instagram',
      'Deposits — collect at booking, reduce last-minute cancels',
      'Automated SMS reminders — no-shows down 70%+',
      'AI re-engagement — personalized messages at 3 and 6 weeks',
      'Client notes — color formulas, preferences, history',
      '$0 commission — flat rate, nothing else',
    ],
    cta: 'Claim Your Stylist Spot',
    ogImage: '/og-stylists.png',
    ogTitle: 'Built for Hair Stylists — Mane',
    ogDesc: 'Run your suite like a real business. Booking, deposits, client history in one place. $29/mo. $0 commission.',
    accent: '#F472B6',
  },
  estheticians: {
    label: 'Estheticians',
    headline: 'Built for Estheticians.',
    sub: 'Focus on your clients, not the paperwork. Booking, intake, reminders, and follow-ups — handled.',
    pain: 'Estheticians spend hours on admin that should run automatically. Clients fall off because no one follows up.',
    features: [
      'Online booking — clean, professional, mobile-first',
      'Client intake notes — capture preferences before the appointment',
      'Automated SMS reminders — before every visit',
      'AI follow-ups — personalized messages between appointments',
      'Service management — set your menu, duration, pricing',
      '$0 commission — your income is 100% yours',
    ],
    cta: 'Claim Your Esthetician Spot',
    ogImage: '/og-estheticians.png',
    ogTitle: 'Built for Estheticians — Mane',
    ogDesc: 'Focus on your clients, not the paperwork. Booking, intake notes, and AI follow-ups for estheticians. $29/mo.',
    accent: '#34D399',
  },
  coaches: {
    label: 'Coaches',
    headline: 'Built for Coaches.',
    sub: 'Book more sessions. Manage your clients. Automate your follow-ups. Grow without the chaos.',
    pain: 'Coaches lose clients between sessions because there\'s no system keeping the relationship warm.',
    features: [
      'Online booking — session scheduling without back-and-forth',
      'Automated reminders — clients show up prepared',
      'AI re-engagement — check-ins and rebook messages that feel personal',
      'Client history — track goals, progress, and notes',
      'Analytics — see which services and clients drive your revenue',
      '$0 commission — no platform taking a percentage of your sessions',
    ],
    cta: 'Claim Your Coach Spot',
    ogImage: '/og-coaches.png',
    ogTitle: 'Built for Coaches — Mane',
    ogDesc: 'Book more sessions. Automated follow-ups and client management for coaches and trainers. $29/mo. $0 commission.',
    accent: '#FBBF24',
  },
};

export default function ProfessionLanding({ profession }) {
  const p = PROFESSIONS[profession] || PROFESSIONS['barbers'];
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post('/barber-waitlist', { email, source: profession });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>{p.ogTitle}</title>
        <meta property="og:title" content={p.ogTitle} />
        <meta property="og:description" content={p.ogDesc} />
        <meta property="og:image" content={`https://joinmane.com${p.ogImage}`} />
        <meta property="og:url" content={`https://joinmane.com/${profession}`} />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`https://joinmane.com${p.ogImage}`} />
      </Helmet>

      <div style={{ background: '#050810', minHeight: '100vh', color: '#fff', fontFamily: 'Inter, -apple-system, sans-serif' }}>

        {/* NAV */}
        <nav style={{ padding: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${BORDER}` }}>
          <Link to="/" style={{ fontSize: '13px', fontWeight: 900, letterSpacing: '0.15em', color: BLUE, textDecoration: 'none' }}>MANE</Link>
          <Link to="/founding" style={{ padding: '10px 20px', background: BLUE, color: '#fff', borderRadius: '8px', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }}>
            Get Early Access
          </Link>
        </nav>

        {/* HERO */}
        <div style={{ maxWidth: '780px', margin: '0 auto', padding: '72px 28px 56px', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', padding: '5px 16px', borderRadius: '20px', background: `${p.accent}18`, border: `1px solid ${p.accent}40`, color: p.accent, fontSize: '11px', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '28px' }}>
            For {p.label}
          </div>

          <h1 style={{ fontSize: 'clamp(38px,6vw,64px)', fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1.05, marginBottom: '20px' }}>
            {p.headline}
          </h1>
          <p style={{ fontSize: 'clamp(16px,2vw,20px)', color: OFF_WHITE, lineHeight: 1.65, maxWidth: '560px', margin: '0 auto 40px' }}>
            {p.sub}
          </p>

          {/* WAITLIST FORM */}
          {!submitted ? (
            <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', maxWidth: '480px', margin: '0 auto 16px' }}>
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={{ flex: 1, minWidth: '220px', padding: '14px 16px', background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: '10px', color: '#fff', fontSize: '14px', outline: 'none' }}
              />
              <button type="submit" disabled={loading} style={{ padding: '14px 24px', background: BLUE, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {loading ? '...' : p.cta}
              </button>
            </form>
          ) : (
            <div style={{ padding: '16px 28px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '10px', color: '#10b981', fontWeight: 700, fontSize: '15px', maxWidth: '400px', margin: '0 auto 16px' }}>
              You're on the list. We'll be in touch soon.
            </div>
          )}

          <p style={{ fontSize: '12px', color: '#7A8499' }}>$29/mo after founding period · $0 commission · $0 hidden fees</p>
        </div>

        {/* PAIN POINT */}
        <div style={{ background: SURFACE, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: '32px 28px', textAlign: 'center' }}>
          <p style={{ maxWidth: '620px', margin: '0 auto', fontSize: '15px', color: OFF_WHITE, lineHeight: 1.7 }}>
            <span style={{ color: '#ef4444', fontWeight: 700 }}>The problem: </span>{p.pain}
          </p>
        </div>

        {/* FEATURES */}
        <div style={{ maxWidth: '780px', margin: '0 auto', padding: '64px 28px' }}>
          <h2 style={{ fontSize: 'clamp(22px,3.5vw,34px)', fontWeight: 900, letterSpacing: '-0.02em', textAlign: 'center', marginBottom: '40px' }}>
            Everything you need. Nothing you don't.
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '14px' }}>
            {p.features.map((f, i) => (
              <div key={i} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '18px 20px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ color: p.accent, fontWeight: 900, fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>✓</span>
                <span style={{ fontSize: '14px', color: '#c5cdd8', lineHeight: 1.5 }}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* PRICING SIMPLE */}
        <div style={{ background: SURFACE, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: '56px 28px', textAlign: 'center' }}>
          <div style={{ maxWidth: '480px', margin: '0 auto' }}>
            <div style={{ display: 'inline-block', padding: '4px 14px', borderRadius: '20px', background: `${GOLD}18`, border: `1px solid ${GOLD}40`, color: GOLD, fontSize: '10px', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '20px' }}>
              Limited · 50 Founding Spots
            </div>
            <div style={{ fontSize: 'clamp(42px,6vw,64px)', fontWeight: 900, letterSpacing: '-0.04em', marginBottom: '4px' }}>$199</div>
            <div style={{ color: OFF_WHITE, fontSize: '15px', marginBottom: '8px' }}>one time · then $29/mo locked forever</div>
            <div style={{ color: '#7A8499', fontSize: '13px', marginBottom: '32px' }}>Standard price is $59/mo — founding members pay $29 forever</div>
            <Link to="/founding" style={{ display: 'inline-block', padding: '16px 40px', background: BLUE, color: '#fff', borderRadius: '10px', fontSize: '15px', fontWeight: 800, textDecoration: 'none', letterSpacing: '0.03em' }}>
              Claim Your Spot
            </Link>
          </div>
        </div>

        {/* FOOTER */}
        <div style={{ padding: '32px 28px', textAlign: 'center', display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/" style={{ color: OFF_WHITE, fontSize: '13px', textDecoration: 'none' }}>← Home</Link>
          <Link to="/pricing" style={{ color: OFF_WHITE, fontSize: '13px', textDecoration: 'none' }}>Pricing</Link>
          <Link to="/about" style={{ color: OFF_WHITE, fontSize: '13px', textDecoration: 'none' }}>About</Link>
        </div>

      </div>
    </>
  );
}
